import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react';
import { PLACEHOLDER, coverOf } from './inventoryUtils';
import { PlayIcon } from './SpecIcons';

const Lightbox = ({ images, index, setIndex, onClose, label }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setIndex((i) => (i + 1) % images.length);
      if (e.key === 'ArrowLeft') setIndex((i) => (i - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [images.length, onClose, setIndex]);
  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col" onClick={onClose} data-testid="vehicle-lightbox">
      <div className="flex items-center justify-between p-4 text-white">
        <span className="text-sm">{label} — {index + 1} / {images.length}</span>
        <button type="button" onClick={onClose} className="p-2 hover:bg-white/10 rounded-full" data-testid="lightbox-close"><X className="w-7 h-7" /></button>
      </div>
      <div className="flex-1 relative flex items-center justify-center px-14" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={() => setIndex((index - 1 + images.length) % images.length)} className="absolute left-3 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white" data-testid="lightbox-prev"><ChevronLeft className="w-7 h-7" /></button>
        <img src={images[index]} alt="" className="max-h-[75vh] max-w-full object-contain" />
        <button type="button" onClick={() => setIndex((index + 1) % images.length)} className="absolute right-3 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white" data-testid="lightbox-next"><ChevronRight className="w-7 h-7" /></button>
      </div>
      <div className="flex gap-2 overflow-x-auto p-4 justify-center" onClick={(e) => e.stopPropagation()}>
        {images.map((src, i) => (
          <button type="button" key={src + i} onClick={() => setIndex(i)} className={`shrink-0 w-20 h-14 rounded-md overflow-hidden border-2 ${i === index ? 'border-[#4ade80]' : 'border-transparent opacity-60 hover:opacity-100'}`}>
            <img src={src} alt="" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
};

export const VehicleGallery = ({ vehicle, en }) => {
  const images = vehicle.images?.length ? vehicle.images : [coverOf(vehicle)];
  const [sel, setSel] = useState(vehicle.video_url ? -1 : 0);
  const [open, setOpen] = useState(false);
  const touch = useRef({ x: 0, swiping: false });
  const label = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
  const showVideo = sel === -1 && vehicle.video_url;

  const onTouchEnd = (e) => {
    const dx = touch.current.x - e.changedTouches[0].clientX;
    if (Math.abs(dx) > 50) setSel((s) => Math.max(0, Math.min(images.length - 1, s + (dx > 0 ? 1 : -1))));
    setTimeout(() => { touch.current.swiping = false; }, 0);
  };

  return (
    <>
      {open && <Lightbox images={images} index={Math.max(sel, 0)} setIndex={setSel} onClose={() => setOpen(false)} label={label} />}
      <div
        className="relative rounded-lg overflow-hidden cursor-pointer group aspect-[4/3] bg-slate-100"
        style={{ touchAction: 'pan-y' }}
        onClick={() => { if (!showVideo && !touch.current.swiping) setOpen(true); }}
        onTouchStart={(e) => { touch.current = { x: e.touches[0].clientX, swiping: false }; }}
        onTouchMove={(e) => { if (Math.abs(touch.current.x - e.touches[0].clientX) > 10) touch.current.swiping = true; }}
        onTouchEnd={onTouchEnd}
        data-testid="main-image-container"
      >
        {showVideo ? (
          <video src={vehicle.video_url} poster={images[0]} controls autoPlay muted loop playsInline preload="metadata" className="w-full h-full object-cover" data-testid="vehicle-main-video" />
        ) : (
          <img src={images[Math.max(sel, 0)]} alt={label} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02] select-none" draggable="false" data-testid="vehicle-main-image" onError={(e) => { e.currentTarget.src = PLACEHOLDER; }} />
        )}
        {!showVideo && (
          <>
            <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs font-medium px-2 py-1 rounded-full">{Math.max(sel, 0) + 1} / {images.length}</div>
            <div className="absolute bottom-3 left-3 bg-black/60 text-white text-xs px-2 py-1 rounded-full sm:hidden">← {en ? 'Swipe' : 'Glisser'} →</div>
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center pointer-events-none">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 px-4 py-2 rounded-full flex items-center gap-2 shadow-lg">
                <ZoomIn className="h-5 w-5 text-slate-700" /><span className="text-sm font-medium text-slate-700">{en ? 'Click to enlarge' : 'Cliquer pour agrandir'}</span>
              </div>
            </div>
          </>
        )}
      </div>
      <div className="flex gap-2 overflow-x-auto pb-2 mt-3">
        {vehicle.video_url && (
          <button type="button" onClick={() => setSel(-1)} className={`relative shrink-0 w-24 h-16 rounded-md overflow-hidden border-2 bg-slate-800 ${sel === -1 ? 'border-[#1a5c3b]' : 'border-transparent'}`} data-testid="thumbnail-video">
            <img src={images[0]} alt="" className="w-full h-full object-cover opacity-60" />
            <span className="absolute inset-0 flex items-center justify-center text-white"><PlayIcon className="h-6 w-6" /></span>
          </button>
        )}
        {images.length > 1 && images.map((src, i) => (
          <button type="button" key={src + i} onClick={() => setSel(i)} className={`shrink-0 w-24 h-16 rounded-md overflow-hidden border-2 transition-colors ${sel === i ? 'border-[#1a5c3b]' : 'border-transparent hover:border-slate-300'}`} data-testid={`thumbnail-${i}`}>
            <img src={src} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          </button>
        ))}
      </div>
    </>
  );
};
